import { useState, useEffect } from 'react'
import Button from '../ui/Button'
import Icon from '../ui/Icon'
import { CALL_STATUSES } from '../../data/constants.jsx'

/**
 * StatusSelector Component
 * Handles call status selection and updates
 * Isolated for better testing and maintainability
 */
const StatusSelector = ({
  onStatusUpdate,
  loading,
  trackingId,
  phoneNumber,
  onStatusSelected,
  disabled = false
}) => {
  const [selectedStatus, setSelectedStatus] = useState('')
  const [isStatusSubmitted, setIsStatusSubmitted] = useState(false)

  // Reset state when a new number is loaded (trackingId changes)
  useEffect(() => {
    if (trackingId && phoneNumber) {
      setSelectedStatus('')
      setIsStatusSubmitted(false)
    }
  }, [trackingId, phoneNumber])

  const handleStatusSelect = (statusId) => {
    if (isStatusSubmitted || disabled) return // Prevent changing status after submission or if disabled
    setSelectedStatus(statusId)
    onStatusSelected?.(statusId)
  }

  const handleUpdateStatus = async () => {
    if (!selectedStatus || !trackingId || isStatusSubmitted) return
    
    try {
      await onStatusUpdate(trackingId, selectedStatus, phoneNumber)
      // Mark as submitted instead of resetting
      setIsStatusSubmitted(true)
    } catch (err) {
      console.error('Failed to update status:', err)
    }
  }

  return (
    <div className="space-y-3">
      <h4 className="font-medium text-gray-800">
        {disabled ? 'প্রথমে কল বাটনে ক্লিক করুন' : isStatusSubmitted ? 'স্ট্যাটাস আপডেট হয়েছে:' : 'কল এর অবস্থা নির্বাচন করুন:'}
      </h4>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
        {CALL_STATUSES.filter(status => status.id !== 'none').map((status) => (
          <Button
            key={status.id}
            onClick={() => handleStatusSelect(status.id)}
            variant={selectedStatus === status.id ? 'primary' : 'outline'}
            disabled={isStatusSubmitted || disabled}
            className={`text-sm py-2 ${
              selectedStatus === status.id && isStatusSubmitted
                ? 'bg-green-600 hover:bg-green-600 text-white border-green-600'
                : ''
            }`}
          >
            <Icon name={status.icon} className="mr-1 text-xs" />
            {status.label}
            {selectedStatus === status.id && isStatusSubmitted && (
              <Icon name="CheckCircle" className="ml-2 text-xs" />
            )}
          </Button>
        ))}
      </div>

      <div className="text-center pt-2">
        <Button
          onClick={handleUpdateStatus}
          disabled={loading || isStatusSubmitted || !selectedStatus || disabled}
          className={`px-6 py-2 ${
            isStatusSubmitted
              ? 'bg-green-600 hover:bg-green-600 text-white cursor-not-allowed'
              : 'bg-green-600 hover:bg-green-700 text-white'
          }`}
        >
          {loading ? (
            <>
              <Icon name="Clock" className="mr-2 animate-spin" />
              আপডেট হচ্ছে...
            </>
          ) : isStatusSubmitted ? (
            <>
              <Icon name="CheckCircle" className="mr-2" />
              আপডেট সম্পন্ন হয়েছে
            </>
          ) : (
            <>
              <Icon name="CheckCircle" className="mr-2" />
              স্ট্যাটাস আপডেট করুন
            </>
          )}
        </Button>
      </div>
    </div>
  )
}

export default StatusSelector